import { ChangeDetectionStrategy, Component, DestroyRef, effect, ElementRef, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { BomListCriteria, BomListState, BomListStore, PAGE_SIZE } from './bom-list.store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, map, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { ActivatedRoute, ParamMap, Params, Router, RouterLink } from "@angular/router";
import { TYPE_LABELS } from '../../tree-summary/tree-summary.component';
import { DropdownComponent, ItemSelectedEvent } from '../../dropdown/dropdown.component';
import { BomListItemDto } from './models/bom-list-item.dto';
import { SessionStore } from '../../../services/session.store';
import { ItemPhotoComponent } from '../../item-photo/item-photo.component';
import { PagerEntry, pagerPages } from './pager-pages';
import { BOM_STRUCTURES, BomStructure, STRUCTURE_LABELS } from '../../../models/bom-structure';

interface TypeFilterOption {
  type: BomListItemDto['type'] | undefined;
  label: string;
}

const TYPE_FILTER_OPTIONS: TypeFilterOption[] = [
  { type: undefined, label: 'Wszystkie' },
  { type: 'product', label: TYPE_LABELS['product'] },
  { type: 'module', label: TYPE_LABELS['module'] },
  { type: 'material', label: TYPE_LABELS['material'] },
];

interface StructureFilterOption {
  structure: BomStructure | undefined;
  label: string;
}

// All five, not just Normalne/Kupione: the column can show Fantom, so the
// filter must reach it (0095)
const STRUCTURE_FILTER_OPTIONS: StructureFilterOption[] = [
  { structure: undefined, label: 'Wszystkie' },
  ...BOM_STRUCTURES.map(structure => ({ structure, label: STRUCTURE_LABELS[structure] })),
];

// The list's criteria live in the query string (0092). Defaults are never
// written: /bom with no parameters is the default view, so the sidenav's
// link resets the list. Absent type is Produkt (0037), so Wszystkie is
// spelled type=all; anything unknown reads as absent and is never forwarded
// to the API, which would answer 400
function parseCriteria(params: ParamMap): BomListCriteria {
  const page = Number(params.get('page'));

  return {
    search: (params.get('search') ?? '').trim(),
    typeFilter: parseType(params.get('type')),
    structureFilter: parseStructure(params.get('structure')),
    page: Number.isInteger(page) && page >= 1 ? page : 1,
  };
}

function parseType(type: string | null): BomListCriteria['typeFilter'] {
  switch (type) {
    case 'module':
    case 'material':
      return type;
    case 'all':
      return undefined;
    default:
      return 'product';
  }
}

// Absent is Wszystkie, so no spelling of its own; the address is lower-case
// like type=module
function parseStructure(structure: string | null): BomListCriteria['structureFilter'] {
  return BOM_STRUCTURES.find(known => known.toLowerCase() === structure?.toLowerCase());
}

function structureParam(structure: BomListCriteria['structureFilter']): string | null {
  return structure?.toLowerCase() ?? null;
}

// null removes the parameter under queryParamsHandling: 'merge'
function typeParam(type: BomListCriteria['typeFilter']): string | null {
  if (type === 'product') {
    return null;
  }

  return type ?? 'all';
}

function pageParam(page: number): number | null {
  return page >= 2 ? page : null;
}

function criteriaParams(criteria: BomListCriteria): Params {
  return {
    search: criteria.search || null,
    type: typeParam(criteria.typeFilter),
    structure: structureParam(criteria.structureFilter),
    page: pageParam(criteria.page),
  };
}

function sameCriteria(a: BomListCriteria, b: BomListCriteria): boolean {
  return a.search === b.search
    && a.typeFilter === b.typeFilter
    && a.structureFilter === b.structureFilter
    && a.page === b.page;
}

@Component({
  selector: 'bom-list-page',
  templateUrl: './bom-list.page.html',
  styleUrl: './bom-list.page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, RouterLink, DropdownComponent, ItemPhotoComponent]
})
export class BomListPage implements OnInit, OnDestroy {

  readonly state$: Observable<BomListState>;
  readonly typeLabels = TYPE_LABELS;
  readonly structureLabels = STRUCTURE_LABELS;
  readonly pageSize = PAGE_SIZE;

  // the list's shadow while it loads (0128): five rows at the row's height
  readonly shadowRows = [0, 1, 2, 3, 4];

  private readonly searchTerm$ = new Subject<string>();
  private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('search');
  private readonly listTable = viewChild<ElementRef<HTMLDivElement>>('listTable');
  private readonly urlSearch = signal('');
  // The last term the box itself wrote to the URL: its echo must not
  // overwrite what the user has typed since
  private writtenSearch: string | undefined;

  constructor(
    private store: BomListStore,
    private sessionStore: SessionStore,
    private route: ActivatedRoute,
    private router: Router,
    private destroyRef: DestroyRef
  ) {
    this.state$ = this.store.state$;

    // The box shows the URL's search: on a shared link's first render, and
    // when the URL changes under the live page (the sidenav's BOM clears it).
    // It stays an uncontrolled input, a [value] binding would set it back to
    // an older term while the user is still typing
    effect(() => {
      const input = this.searchInput();
      const term = this.urlSearch();

      if (input && term !== this.writtenSearch) {
        input.nativeElement.value = term;
      }
    });
  }

  ngOnInit(): void {
    // The URL is the single source of truth for the criteria: gestures write
    // it and this one stream reads it. switchMap drops the in-flight request
    // when newer criteria arrive, so a stale response can never overwrite
    // fresher results
    this.route.queryParamMap
      .pipe(
        map(params => {
          const criteria = parseCriteria(params);

          this.urlSearch.set(criteria.search);
          this.normalizeUrl(params, criteria);

          return criteria;
        }),
        // the normalized URL parses to the same criteria, no second request
        distinctUntilChanged(sameCriteria),
        switchMap(criteria => {
          // a new page is read from its top
          const pageChanged = criteria.page !== this.store.state.page;

          return this.store.load(criteria).pipe(tap(() => {
            this.clampPage();

            if (pageChanged) {
              this.scrollToTop();
            }
          }));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    // No distinctUntilChanged here: after the sidenav cleared the box,
    // retyping the previous term must be written again. The router ignores
    // a write that matches the URL, and the stream above loads only on change
    this.searchTerm$
      .pipe(
        debounceTime(300),
        map(term => term.trim()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(term => {
        this.writtenSearch = term;
        // a new question starts at its first answer
        this.navigate({ search: term || null, page: null });
      });
  }

  // the import page is gated the same way (permissionGuard in app.routes.ts)
  canWriteBom(): boolean {
    return this.sessionStore.can('BomWrite');
  }

  onSearchInput(term: string): void {
    this.searchTerm$.next(term);
  }

  searchTypeOptions = (searchTerm: string) => of(
    TYPE_FILTER_OPTIONS.filter(option => option.label.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  formatTypeOption = (option: TypeFilterOption) => option.label;

  onTypeSelected(event: ItemSelectedEvent<TypeFilterOption>): void {
    // Enter on a raw search term selects nothing — the filter only has
    // its four fixed options
    if (!event.item) {
      return;
    }

    this.navigate({ type: typeParam(event.item.type), page: null });
  }

  searchStructureOptions = (searchTerm: string) => of(
    STRUCTURE_FILTER_OPTIONS.filter(option => option.label.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  formatStructureOption = (option: StructureFilterOption) => option.label;

  onStructureSelected(event: ItemSelectedEvent<StructureFilterOption>): void {
    if (!event.item) {
      return;
    }

    this.navigate({ structure: structureParam(event.item.structure), page: null });
  }

  pagerPages(state: BomListState): PagerEntry[] {
    return pagerPages(state.page, this.pageCount(state));
  }

  pageCount(state: BomListState): number {
    return Math.ceil(state.totalCount / PAGE_SIZE);
  }

  // the pager's links merge this into the current criteria
  pageParams(page: number): Params {
    return { page: pageParam(page) };
  }

  // A stale or pasted link (bad type, structure or page) is rewritten to the view it
  // shows, so the address always says what is on screen
  private normalizeUrl(params: ParamMap, criteria: BomListCriteria): void {
    const canonical = criteriaParams(criteria);
    const differs = Object.entries(canonical)
      .some(([key, value]) => params.get(key) !== (value === null ? null : String(value)));

    if (differs) {
      this.navigate(canonical);
    }
  }

  // A page past the end (items deleted, the link is a week old) moves to the
  // last page that exists; an empty result to page 1
  private clampPage(): void {
    const state = this.store.state;
    const lastPage = Math.max(1, this.pageCount(state));

    if (state.page > lastPage) {
      this.navigate({ page: pageParam(lastPage) });
    }
  }

  // Every list gesture replaces the history entry: the list is one entry,
  // whatever was typed or paged, so Wstecz always leaves it
  private navigate(queryParams: Params): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  // The table is the scroll box (0103), the page itself never scrolls. The old
  // rows stay mounted while the next page loads, so the element is there; on
  // the first load it holds the shadow rows (0128), and there is nothing to scroll
  private scrollToTop(): void {
    const table = this.listTable();

    if (table) {
      table.nativeElement.scrollTop = 0;
    }
  }

  ngOnDestroy(): void {
    this.store.destroy();
  }
}
